/** ExhibitB is a table view in survey v2 submission form for users */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FormattedMessage } from 'react-intl';
import AnswerSummary from '../../../digitalTrial/AnswerSummary';
import {
  DigitalCase,
  DigitalQuestion,
  DigitalSection,
  DigitalSubmission,
  DigitalSubmissionMeta,
  Enrollment,
  FullSubmissionMap,
  QuestionCaseSetting,
  Trial,
} from '../../../../redux/types';
import usePendingCompulsoryQuestions from '../../../../hooks/survey/usePendingCompulsoryQuestions';
import GenericDialog from '../../../GenericDialog';
import QuestionInputForm from '../QuestionInputForm';
import {
  selectSettingsDictionaryByQuestionAndCase,
} from '../../../../redux/reducers/SurveyDataReducer';
import ContextQuestionInputForm from '../ContextQuestionInputForm';
import usePendingCompulsoryCtxQuestions from '../../../../hooks/survey/usePendingCompulsoryCtxQuestions';
import Prompt from '../../../layout/Prompt';
import { QuestionSaveOp } from '../../../../hooks/survey/useQuestionSaveStatus';
import QuestionInputFormPreview from '../QuestionInputFormPreview';
import SectionOptOut from '../SectionOptOut';
import SectionOptOutPreview from '../SectionOptOutPreview';
import ReferenceLinks from '../ReferenceLinks';
import usePreventUnsavedTabClose from '../../../../hooks/survey/usePreventUnsavedTabClose';

type Props = {
  trial?: Trial;
  enrollment?: Enrollment;
  cases: DigitalCase[];
  questions: DigitalQuestion[];
  contextQuestions: DigitalQuestion[];
  sections: DigitalSection[];
  selectedSection?: DigitalSection;
  submissionsMapByQuestionAndCaseId: Record<number, Record<number, DigitalSubmission> | undefined>;
  contextSubmissionMapByQuestionId: Record<number, DigitalSubmission | undefined>;
  settingsDictionary: ReturnType<typeof selectSettingsDictionaryByQuestionAndCase>;
  dsms?: DigitalSubmissionMeta[];
  sectionSubmissionMeta?: DigitalSubmissionMeta;
  fullSubmissionMap: FullSubmissionMap;
  fullCtxSubmissionMap: Record<number, Record<number, DigitalSubmission>>;
  questionCaseSettings: QuestionCaseSetting[];
  disabled: boolean;
  isPublicView: boolean;
  showEnrollmentsSubmissions?: boolean;
  isEditorPrivileged: boolean;
  setSelectedDigitalSection: (sectionId?: number) => void;
  onAddOrUpdateDigitalSubmission: (submission: DigitalSubmission) => void;
  finalizeSubmissions: () => Promise<void>;
  optOutFromSection: (
    trialId: number, sectionId: number, enrollmentId: number, isOptOut: boolean
  ) => void;
  showErrorNotification: (message: string) => void;
};

type SelectedBufferType = {
  questionId: number;
  caseId: number;
}

const QuestionnaireExhibitB = ({
  trial,
  enrollment,
  cases,
  questions,
  contextQuestions,
  sections,
  selectedSection,
  submissionsMapByQuestionAndCaseId,
  contextSubmissionMapByQuestionId,
  settingsDictionary,
  dsms,
  sectionSubmissionMeta,
  fullSubmissionMap,
  fullCtxSubmissionMap,
  questionCaseSettings: settings,
  disabled: disabledSurveyInput,
  isPublicView,
  showEnrollmentsSubmissions,
  setSelectedDigitalSection,
  onAddOrUpdateDigitalSubmission,
  finalizeSubmissions,
  optOutFromSection,
  isEditorPrivileged,
  showErrorNotification,
}: Props): JSX.Element => {
  const [incompleteSettingMap, pendingSecId] = usePendingCompulsoryQuestions(
    settings, fullSubmissionMap, dsms,
  );
  const [incompleteCtxSettingMap, pendingCtxSecId] = usePendingCompulsoryCtxQuestions(
    settings, fullCtxSubmissionMap, dsms,
  );
  const trackFocusAndPreventClose = usePreventUnsavedTabClose();
  const [selectedBuffer, setSelectedBuffer] = useState<SelectedBufferType>();
  const [promptFinalize, setPromptFinalize] = useState<boolean>(false);
  const [questionSaveStatus, setQuestionSaveStatus] = useState<QuestionSaveOp>('idle');
  const [scrollToTop, setScrollToTop] = useState<boolean>(false);

  const handleReviewTabSwitch = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedDigitalSection(newValue);
  };

  const onCaseClick = useCallback((questionId: number, caseId: number) => {
    setSelectedBuffer({
      caseId,
      questionId,
    });
  }, []);

  const getSaveStatusCb = useCallback((op: QuestionSaveOp) => {
    setQuestionSaveStatus(op);
  }, []);

  const getSaveStatusContextCb = useCallback((op: QuestionSaveOp, qId: number, cId?: number) => {
    const trackMap = trackFocusAndPreventClose(op, qId, cId);
    const isSaving = Object.values(trackMap).find((v) => v === 'debounceStarting' || v === 'saving');
    setQuestionSaveStatus(isSaving ? 'saving' : 'saved');
  }, [trackFocusAndPreventClose]);

  const onSave = (submission: DigitalSubmission) => {
    setSelectedBuffer(undefined);
    onAddOrUpdateDigitalSubmission(submission);
  };

  useEffect(() => {
    if (scrollToTop) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
      setScrollToTop(false);
    }
  }, [scrollToTop]);

  const finalizeConfirmHandler = (finalize: boolean) => {
    if (questionSaveStatus === 'saving') {
      showErrorNotification('Saving data, please wait a moment.');
      return;
    }
    setPromptFinalize(false);
    if ((!!incompleteSettingMap || !!incompleteCtxSettingMap) || finalize === false) {
      const pendingSec = pendingSecId || pendingCtxSecId;
      if (pendingSec) {
        setSelectedDigitalSection(pendingSec);
        setScrollToTop(true);
      }
      return;
    }
    finalizeSubmissions();
  };

  const selectedQuestion = questions.find((q) => q.id === selectedBuffer?.questionId);
  const isSurveyIncomplete = !!incompleteSettingMap || !!incompleteCtxSettingMap;
  const confirmationTextId = isSurveyIncomplete ? 'survey.goToIncompleteSection' : 'registration.submit';

  return (
    <Box>
      <Box sx={{ mx: 2 }} display="flex" justifyContent="flex-end">
        { selectedSection?.canOptOut && isPublicView && (<SectionOptOutPreview />) }
        {
          selectedSection?.canOptOut && trial && enrollment && sectionSubmissionMeta && (
            <SectionOptOut
              trial={trial}
              enrollment={enrollment}
              selectedSection={selectedSection}
              sectionSubmissionMeta={sectionSubmissionMeta}
              optOutFromSection={optOutFromSection}
              isEditorPrivileged={isEditorPrivileged}
              showSubmissions={showEnrollmentsSubmissions}
            />
          )
        }
      </Box>
      {
        selectedSection && (
          <Box sx={{ mt: 2 }} display="flex" gap={2}>
            <Tabs
              sx={{ flexGrow: 1 }}
              value={selectedSection?.id}
              onChange={handleReviewTabSwitch}
              variant="scrollable"
              scrollButtons
            >
              {
                sections.map((s) => <Tab label={s.name} key={s.id} value={s.id} />)
              }
            </Tabs>
          </Box>
        )
      }
      <Box aria-label="reference-link-container">
        {
          selectedSection && selectedSection.referenceLinks && (
            <Box sx={{ m: 2, p: 2, bgcolor: 'grey.100' }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                <FormattedMessage id="referencelink.title" />
              </Typography>
              <ReferenceLinks
                selectedSection={selectedSection}
              />
            </Box>
          )
        }
      </Box>
      <Box>
        <AnswerSummary
          digitalCases={cases}
          digitalQuestions={questions}
          submissionsMapByQuestionAndCaseId={submissionsMapByQuestionAndCaseId}
          compulsorySettingsMapByQuestionAndCaseId={incompleteSettingMap}
          settingsDictionary={settingsDictionary}
          onCaseClick={onCaseClick}
        />
      </Box>
      <Box>
        {
          !!contextQuestions.length && (
            <Typography variant="h6" sx={{ p: 3, mt: 2 }}>
              <FormattedMessage id="survey.methodologicalSelector" />
            </Typography>
          )
        }
        {
          trial && selectedSection
          && enrollment && !isPublicView && (
            <ContextQuestionInputForm
              trialId={trial.id}
              sectionId={selectedSection.id}
              enrollmentId={enrollment.id}
              questions={contextQuestions}
              contextSubmissionByQuestionId={contextSubmissionMapByQuestionId}
              addOrUpdateDigitalSubmission={onAddOrUpdateDigitalSubmission}
              disabled={disabledSurveyInput}
              compulsoryCtxSettingMap={incompleteCtxSettingMap}
              showErrorNotification={showErrorNotification}
              getSaveStatus={getSaveStatusContextCb}
              saveOnBlur
            // hilightedQuestionId={highlightedQuestion}
            // setHilightedQuestion={setHilightedQuestion}
            />
          )
        }
        {
          isPublicView && (
            <QuestionInputFormPreview
              questions={contextQuestions}
              settingsDictionary={settingsDictionary}
            />
          )
        }
      </Box>
      <Box sx={{ m: 2 }} display="flex" justifyContent="flex-end">
        <Button
          disabled={sectionSubmissionMeta?.submissionFinalized
            || showEnrollmentsSubmissions || isPublicView}
          onClick={() => setPromptFinalize(true)}
          variant="contained"
        >
          <FormattedMessage id="survey.finaliseDialogHeading" />
        </Button>
      </Box>
      {/* Question Input Dialog */}
      <GenericDialog
        isOpen={!!selectedBuffer}
        onRequestClose={() => {
          if (questionSaveStatus === 'saving' || questionSaveStatus === 'debounceStarting') {
            return;
          }
          setSelectedBuffer(undefined);
        }}
      >
        <Box sx={{ m: 1 }}>
          <Box display="flex" justifyContent="end">
            <Box
              sx={{ cursor: 'pointer' }}
              onClick={
                () => {
                  if (questionSaveStatus === 'saving' || questionSaveStatus === 'debounceStarting') {
                    return;
                  }
                  setSelectedBuffer(undefined);
                }
              }
            >
              <CloseIcon />
            </Box>
          </Box>
          <Typography sx={{ mx: '1rem' }} color="primary.dark" variant="h4">
            {cases.find((c) => c.id === selectedBuffer?.caseId)?.name}
          </Typography>
          {
            isPublicView && selectedQuestion && selectedBuffer && (
              <QuestionInputFormPreview
                questions={[selectedQuestion]}
                caseId={selectedBuffer.caseId}
                settingsDictionary={settingsDictionary}
              />
            )
          }
          {
            trial && enrollment
            && selectedSection && selectedBuffer
            && selectedQuestion && !isPublicView && (
              <QuestionInputForm
                trialId={trial.id}
                caseId={selectedBuffer.caseId}
                sectionId={selectedSection.id}
                enrollmentId={enrollment.id}
                questions={[selectedQuestion]}
                submissionsMapByQuestionAndCaseId={submissionsMapByQuestionAndCaseId}
                addOrUpdateDigitalSubmission={onSave}
                disabled={disabledSurveyInput}
                settingsDictionary={settingsDictionary}
                getSaveStatus={getSaveStatusCb}
                showErrorNotification={showErrorNotification}
                manualSave
              />
            )
          }
        </Box>
      </GenericDialog>
      {/* Finalize Prompt */}
      <Prompt
        title={<FormattedMessage id="survey.finaliseDialogHeading" />}
        display={promptFinalize}
        action={finalizeConfirmHandler}
        version={2}
        alert={isSurveyIncomplete}
        okButtonText={<FormattedMessage id={confirmationTextId} />}
      >
        {
          (incompleteSettingMap || incompleteCtxSettingMap) && (
            <Typography color="error">
              <FormattedMessage id="survey.compulsoryWarn" />
            </Typography>
          )
        }
        {
          !incompleteSettingMap && !incompleteCtxSettingMap && (
            <Typography>
              <FormattedMessage id="survey.submitInfo" />
            </Typography>
          )
        }
      </Prompt>
    </Box>
  );
};

export default QuestionnaireExhibitB;
